import { CheckCheck, Download, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { DocumentEntityType, DocumentType } from "../../../../../src/api/caseDocuments";
import { EventType } from "../../../../api/events";
import { RecoveryDetails } from "../../../../api/recovery";
import { useAppSelector } from "../../../../app/store";
import ButtonV2 from "../../../../components/ui/button/v2";
import DocumentTooltip from "../../../../features/documentManagement/components/DocumentTooltip";
import { AddEventModal } from "../../../../features/timeline/components/AddEventModal";
import { cn } from "../../../../lib/utils";
import ReportDocuments from "../../../documentManagement/components/ReportDocuments";
import { selectEventByTypeAndEntityId } from "../../../timeline/logic/slice";
import { selectRecoveryDetailsByLoanReferenceId } from "../../logic/recoverySlice";
import { getAccountProductInfoByAccountNumber } from "../../../creditAccounts/logic/slice";
import ReportFormTable, { EventDateInputCell, ReportFormRow, TextAreaInputCell } from "../common/ReportTableForm";
import ReportToggleSection from "../common/ReportToggleSection";


interface RecoveryReportHeaderProps {
  loanId: string;
  accountName?: string;
  className?: string;
}

const RecoveryReportHeader: React.FC<RecoveryReportHeaderProps> = ({
    loanId,
    accountName,
    className,
  }) => {
    const displayLoanAccountNumber = useAppSelector(
      (state) =>
        getAccountProductInfoByAccountNumber(state, loanId)?.ext_account_number ||
        "--",
    );

    return (
      <div className={cn("px-[10px] py-[6px] bg-gray-50", className)}>
        <div className="flex flex-1 items-center gap-2 py-[6px]">
          {accountName && (
            <>
              <span className="truncate text-[#0A0A0A] font-sans text-lg font-medium leading-7">
                {accountName}
              </span>
              <span className="text-gray-400 font-normal">|</span>
            </>
          )}

          <span className="overflow-hidden line-clamp-1 text-[#737373] font-sans text-lg font-normal leading-7">
            {displayLoanAccountNumber}
          </span>
        </div>
      </div>
    );
};

const RecoveryReportSummary = () => {
    return (
      <div className="px-6 flex flex-col gap-4 bg-white rounded-b-lg">
        <RecoveryReportActionsHeader />

        <div className="text-sm font-semibold leading-5 text-[#737373] font-sans">
          Amount Recovered
        </div>

        <div className="flex gap-6">
          <div className="flex flex-col gap-1">
            <div className="overflow-hidden truncate text-xs font-medium leading-none text-[#737373] font-sans">
              Amount Recovered Post NPA
            </div>

            <DocumentTooltip content="Data unavailable">
              <div className="overflow-hidden truncate text-lg font-semibold leading-7 text-[#0A0A0A] font-sans">
                --
              </div>
            </DocumentTooltip>
          </div>

          <div className="w-px h-11 bg-[#E5E5E5] self-center" />

          <div className="flex flex-col gap-1">
            <div className="overflow-hidden truncate text-xs font-medium leading-none text-[#737373] font-sans">
              Amount Recovered Post Fraud Classification
            </div>

            <DocumentTooltip content="Data unavailable">
              <div className="overflow-hidden truncate text-lg font-semibold leading-7 text-[#0A0A0A] font-sans">
                --
              </div>
            </DocumentTooltip>
          </div>
        </div>

        <div className="pt-3 pb-7 px-1">
          <div className="h-px w-full bg-[#E5E5E5]" />
        </div>
      </div>
    );
};

const RecoveryReportActionsHeader = () => {
    return (
        <div className="flex mt-4 items-center justify-between">
            <div className="text-lg font-semibold leading-7 text-gray-900">
                Recovery Report
            </div>

            <div className="flex items-center gap-3 px-[10px]">
                <ButtonV2
                    variant="outline-primary"
                    size="md"
                    prefix={<CheckCheck className="h-4 w-4" />}
                >
                    Status
                </ButtonV2>

                <ButtonV2
                    variant="outline-primary"
                    size="md"
                    prefix={<Mail className="h-4 w-4" />}
                >
                    Send Email
                </ButtonV2>

                <ButtonV2
                    variant="outline-primary"
                    size="md"
                    prefix={<Download className="h-4 w-4" />}
                >
                    Download
                </ButtonV2>
            </div>
        </div>
    );
};

export interface RecoveryDetailsProps {
    loanReferenceId: string,
    onChange: (dirtyRecoveryDetails?: RecoveryDetails) => void;
    discardChangesTrigger: number
}

export const LoanRecoveryDetails: React.FC<RecoveryDetailsProps> = ({
    loanReferenceId,
    onChange,
    discardChangesTrigger
}) => {

    const activeRecovery = useAppSelector(selectRecoveryDetailsByLoanReferenceId(loanReferenceId));

    const [isSARFAESIOn, setIsSARFAESIOn] = useState(false);
    const [sarfaesiStatus, setSarfaesiStatus] = useState("");
    const sarfaesiNotice13_2Event = useAppSelector(selectEventByTypeAndEntityId(EventType.SARFAESI_NOTICE_13_2_ISSUED, loanReferenceId));
    const sarfaesiNotice13_4Event = useAppSelector(selectEventByTypeAndEntityId(EventType.SARFAESI_NOTICE_13_4_ISSUED, loanReferenceId));
    const physicallyPossessedEvent = useAppSelector(selectEventByTypeAndEntityId(EventType.SECURITY_PHYSICALLY_POSSESSED, loanReferenceId));

    const [isDRTOn, setIsDRTOn] = useState(false);
    const [drtName, setDrtName] = useState("");
    const [drtCaseNumber, setDrtCaseNumber] = useState("");
    const [drtStatus, setDrtStatus] = useState("");
    const drtFiledEvent = useAppSelector(selectEventByTypeAndEntityId(EventType.RECOVERY_SUIT_FILED_IN_DRT, loanReferenceId));

    const [isNCLTOn, setIsNCLTOn] = useState(false);
    const [ncltName, setNcltName] = useState("");
    const [ncltCaseNumber, setNcltCaseNumber] = useState("");
    const [ncltStatus, setNcltStatus] = useState("");
    const ncltFiledEvent = useAppSelector(selectEventByTypeAndEntityId(EventType.CASE_FILED_IN_NCLT, loanReferenceId));

    const [isOtherCourtCaseOn, setIsOtherCourtCaseOn] = useState(false);
    const [otherCourtStatus, setOtherCourtStatus] = useState("");

    const seedFromRedux = () => {
      setIsSARFAESIOn(activeRecovery?.sarfaesi_details?.enabled || false)
      setSarfaesiStatus(activeRecovery?.sarfaesi_details?.status_of_recovery || "");
  
      setIsDRTOn(activeRecovery?.drt_details?.enabled || false)
      setDrtName(activeRecovery?.drt_details?.name || "");
      setDrtCaseNumber(activeRecovery?.drt_details?.case_number || "");
      setDrtStatus(activeRecovery?.drt_details?.status_of_recovery || "");
  
      setIsNCLTOn(activeRecovery?.nclt_details?.enabled || false)
      setNcltName(activeRecovery?.nclt_details?.name || "");
      setNcltCaseNumber(activeRecovery?.nclt_details?.case_number || "");
      setNcltStatus(activeRecovery?.nclt_details?.status_of_recovery || "");
  
      setIsOtherCourtCaseOn(activeRecovery?.other_court_case_details?.enabled || false)
      setOtherCourtStatus(activeRecovery?.other_court_case_details?.status || "");
    };

    useEffect(() => {
      seedFromRedux();
    }, [activeRecovery, discardChangesTrigger]);

    useEffect(() => {
      const baseSarfaesiEnabled = activeRecovery?.sarfaesi_details?.enabled || false;
      const baseSarfaesiStatus = activeRecovery?.sarfaesi_details?.status_of_recovery || "";
  
      const baseDrtEnabled = activeRecovery?.drt_details?.enabled || false;
      const baseDrtName = activeRecovery?.drt_details?.name || "";
      const baseDrtCase = activeRecovery?.drt_details?.case_number || "";
      const baseDrtStatus = activeRecovery?.drt_details?.status_of_recovery || "";
  
      const baseNcltEnabled = activeRecovery?.nclt_details?.enabled || false;
      const baseNcltName = activeRecovery?.nclt_details?.name || "";
      const baseNcltCase = activeRecovery?.nclt_details?.case_number || "";
      const baseNcltStatus = activeRecovery?.nclt_details?.status_of_recovery || "";
  
      const baseOtherCasesEnabled = activeRecovery?.other_court_case_details?.enabled || false;
      const baseOtherCasesStatus = activeRecovery?.other_court_case_details?.status || "";
  
      const isDirty =  (
        baseSarfaesiEnabled !== isSARFAESIOn ||
        sarfaesiStatus !== baseSarfaesiStatus ||
        baseDrtEnabled !== isDRTOn ||
        drtName !== baseDrtName ||
        drtCaseNumber !== baseDrtCase ||
        drtStatus !== baseDrtStatus ||
        baseNcltEnabled !== isNCLTOn ||
        ncltName !== baseNcltName ||
        ncltCaseNumber !== baseNcltCase ||
        ncltStatus !== baseNcltStatus ||
        baseOtherCasesEnabled !== isOtherCourtCaseOn ||
        otherCourtStatus !== baseOtherCasesStatus
      );

      if(isDirty) {
        const dirtyRecoveryDetails: RecoveryDetails = {
          loan_reference_id: loanReferenceId,
          sarfaesi_details: {
            enabled: isSARFAESIOn,
            status_of_recovery: sarfaesiStatus || null,
          },
          drt_details: {
            enabled: isDRTOn,
            name: drtName || undefined,
            case_number: drtCaseNumber || undefined,
            status_of_recovery: drtStatus || undefined,
          },
          nclt_details: {
            enabled: isNCLTOn,
            name: ncltName || undefined,
            case_number: ncltCaseNumber || undefined,
            status_of_recovery: ncltStatus || undefined,
          },
          other_court_case_details: {
            enabled: isOtherCourtCaseOn,
            status: otherCourtStatus || undefined,
          },
        };

        onChange(dirtyRecoveryDetails);
      } else {
        onChange(undefined);
      }
    }, [
      isSARFAESIOn,
      isDRTOn,
      isNCLTOn,
      isOtherCourtCaseOn,
      activeRecovery,
      sarfaesiStatus,
      drtName,
      drtCaseNumber,
      drtStatus,
      ncltName,
      ncltCaseNumber,
      ncltStatus,
      otherCourtStatus,
    ]);

    const [eventModal, setEventModal] = useState<{
        isOpen: boolean;
        eventType?: EventType;
        entityId?: string;
    }>({ isOpen: false});

    const openAddEventModal = (eventType: EventType) => {
      setEventModal({
        isOpen: true,
        eventType: eventType,
        entityId: loanReferenceId
      });
    };

    const sarfaesiFormRows: ReportFormRow[] = [
      {
        label: "Section 13(2) Demand Notice - Issue Date",
        inputCell: <EventDateInputCell 
          value={sarfaesiNotice13_2Event?.date || undefined} 
          onClick={() => {openAddEventModal(EventType.SARFAESI_NOTICE_13_2_ISSUED)}} 
          disabled={!(sarfaesiNotice13_2Event === undefined || sarfaesiNotice13_2Event.date === undefined)}
        />
      },
      {
        label: "Section 13(4) Possession Notice - Issue Date",
        inputCell: <EventDateInputCell 
          value={sarfaesiNotice13_4Event?.date || undefined} 
          onClick={() => {openAddEventModal(EventType.SARFAESI_NOTICE_13_4_ISSUED)}} 
          disabled={!(sarfaesiNotice13_4Event === undefined || sarfaesiNotice13_4Event.date === undefined)}
        />
      },
      {
        label: "Physical Possession Date",
        inputCell: <EventDateInputCell 
          value={physicallyPossessedEvent?.date || undefined} 
          onClick={() => {openAddEventModal(EventType.SECURITY_PHYSICALLY_POSSESSED)}} 
          disabled={!(physicallyPossessedEvent === undefined || physicallyPossessedEvent.date === undefined)}
        />
      },
      {
        label: "Status of Recovery under SARFAESI Act",
        inputCell: <TextAreaInputCell 
          value={sarfaesiStatus} 
          onChange={setSarfaesiStatus} 
        />
      }
    ]

    const drtFormRows: ReportFormRow[] = [
      {
        label: "Name of DRT",
        inputCell: <input
          value={drtName}
          onChange={(e) => setDrtName(e.target.value)}
          placeholder="Enter DRT name"
          className="w-full bg-transparent font-sans font-normal text-sm leading-5 text-[#0A0A0A] placeholder:text-[#737373] focus:outline-none"
        />
      },
      {
        label: "DRT Case Number",
        inputCell: <input
          value={drtCaseNumber}
          onChange={(e) => setDrtCaseNumber(e.target.value)}
          placeholder="Enter DRT case number"
          className="w-full bg-transparent font-sans font-normal text-sm leading-5 text-[#0A0A0A] placeholder:text-[#737373] focus:outline-none"
        />
      },
      {
        label: "DRT Case Filing Date",
        inputCell: <EventDateInputCell 
          value={drtFiledEvent?.date || undefined} 
          onClick={() => {openAddEventModal(EventType.RECOVERY_SUIT_FILED_IN_DRT)}} 
          disabled={!(drtFiledEvent === undefined || drtFiledEvent.date === undefined)}
        />
      },
      {
        label: "Status of Recovery in DRT",
        inputCell: <TextAreaInputCell 
          value={drtStatus} 
          onChange={setDrtStatus} 
        />
      }
    ]

    const ncltFormRows: ReportFormRow[] = [
      {
        label: "Name of NCLT",
        inputCell: <input
          value={ncltName}
          onChange={(e) => setNcltName(e.target.value)}
          placeholder="Enter NCLT name"
          className="w-full bg-transparent font-sans font-normal text-sm leading-5 text-[#0A0A0A] placeholder:text-[#737373] focus:outline-none"
        />
      },
      {
        label: "NCLT Case Number",
        inputCell: <input
          value={ncltCaseNumber}
          onChange={(e) => setNcltCaseNumber(e.target.value)}
          placeholder="Enter NCLT case number"
          className="w-full bg-transparent font-sans font-normal text-sm leading-5 text-[#0A0A0A] placeholder:text-[#737373] focus:outline-none"
        />
      },
      {
        label: "NCLT Case Filing Date",
        inputCell: <EventDateInputCell 
          value={ncltFiledEvent?.date || undefined} 
          onClick={() => {openAddEventModal(EventType.CASE_FILED_IN_NCLT)}} 
          disabled={!(ncltFiledEvent === undefined || ncltFiledEvent.date === undefined)}
        />
      },
      {
        label: "Status of Recovery in NCLT",
        inputCell: <TextAreaInputCell 
          value={ncltStatus} 
          onChange={setNcltStatus} 
        />
      }
    ]

    const otherCasesFormRows: ReportFormRow[] = [
      {
        label: "Status of Other Court Cases",
        inputCell: <TextAreaInputCell 
          value={otherCourtStatus} 
          onChange={setOtherCourtStatus} 
        />
      }
    ]

    return (
        
        
        <div className="bg-white rounded-xl overflow-hidden">
            <RecoveryReportHeader
                loanId={loanReferenceId}
                accountName={undefined}
            />

            <RecoveryReportSummary />

            <div className="px-6 pb-4 flex flex-col">
                <ReportToggleSection label="SARFAESI" isOn={isSARFAESIOn} onToggle={() => setIsSARFAESIOn((prevState) => !prevState)}
                    >
                    <ReportFormTable rows={sarfaesiFormRows} className="mt-4"/>
                    <ReportDocuments document_type={DocumentType.RECOVERY_SARFAESI} entityId={loanReferenceId}  entityType={DocumentEntityType.LOAN_ACCOUNT}/>
                </ReportToggleSection>

                <ReportToggleSection label="Debt Recovery Tribunal (DRT)" isOn={isDRTOn} onToggle={() => setIsDRTOn((prevState) => !prevState)}
                    >
                    <ReportFormTable rows={drtFormRows} className="mt-4"/>
                    <ReportDocuments document_type={DocumentType.RECOVERY_DRT} entityId={loanReferenceId}  entityType={DocumentEntityType.LOAN_ACCOUNT}/>
                </ReportToggleSection>

                <ReportToggleSection label="National Company Law Tribunal (NCLT)" isOn={isNCLTOn} onToggle={() => setIsNCLTOn((prevState) => !prevState)}
                    >
                    <ReportFormTable rows={ncltFormRows} className="mt-4"/>
                    <ReportDocuments document_type={DocumentType.RECOVERY_NCLT} entityId={loanReferenceId}  entityType={DocumentEntityType.LOAN_ACCOUNT}/>
                </ReportToggleSection>

                <ReportToggleSection label="Other Court Cases" isOn={isOtherCourtCaseOn} onToggle={() => setIsOtherCourtCaseOn((prevState) => !prevState)}
                    >
                    <ReportFormTable rows={otherCasesFormRows} className="mt-4"/>
                    <ReportDocuments document_type={DocumentType.RECOVERY_COURT_CASE}  entityId={loanReferenceId}  entityType={DocumentEntityType.LOAN_ACCOUNT}/>
                </ReportToggleSection>
            </div>

            {eventModal.isOpen && eventModal.eventType && eventModal.entityId && (
                <AddEventModal
                    mode="Edit"
                    disableStageSelect
                    disableEntitySelect
                    readOnlyNote
                    initialData={{
                        eventType: eventModal.eventType,
                        events: [{ entity_id: eventModal.entityId, date: "" }],
                    }}
                    onClose={() => (setEventModal({isOpen: false}))}
                />
            )}
        </div>
    )
}